import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateTime } from '../../utils/locale';
import { useLanguage } from '../../i18n/LanguageContext';

const ReferralsTab = ({ network, earnings = [], earningsTotal = 0, loading }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { locale } = useLanguage();
  const [earningsPage, setEarningsPage] = useState(0);
  const [earningsTypeFilter, setEarningsTypeFilter] = useState('');
  const ITEMS_PER_PAGE = 6;
  const EARNINGS_PER_PAGE = 4;

  // Combine all levels into a single list for pagination
  const allReferrals = network ? [
    ...(network.level1 || []).map(ref => ({ ...ref, level: 1 })),
    ...(network.level2 || []).map(ref => ({ ...ref, level: 2 })),
    ...(network.level3 || []).map(ref => ({ ...ref, level: 3 }))
  ] : [];

  const earningsByReferredId = earnings.reduce((acc, entry) => {
    const referredId = entry.referred_id ?? entry.referredId;
    if (!referredId) {
      return acc;
    }
    const amount = parseFloat(entry.amount || 0);
    acc[referredId] = (acc[referredId] || 0) + (Number.isFinite(amount) ? amount : 0);
    return acc;
  }, {});

  const paginatedReferrals = allReferrals.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const getLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Level 1 (8%)';
      case 2: return 'Level 2 (5%)';
      case 3: return 'Level 3 (2%)';
      default: return 'Unknown';
    }
  };

  const getReferralTotal = (ref) => {
    const referredId = ref.referred_id ?? ref.referredId;
    if (referredId && earningsByReferredId[referredId] !== undefined) {
      return earningsByReferredId[referredId];
    }
    const fallback = parseFloat(ref.total_earned || 0);
    return Number.isFinite(fallback) ? fallback : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }, locale);
  };

  const filteredEarnings = earningsTypeFilter
    ? earnings.filter((entry) => entry.earnings_type === earningsTypeFilter)
    : earnings;

  const paginatedEarnings = filteredEarnings.slice(
    earningsPage * EARNINGS_PER_PAGE,
    (earningsPage + 1) * EARNINGS_PER_PAGE
  );

  const renderEarningsPagination = () => {
    if (filteredEarnings.length <= EARNINGS_PER_PAGE) {
      return null;
    }

    return (
      <div className="flex items-center justify-center space-x-4 mt-6">
        <button
          onClick={() => setEarningsPage(Math.max(0, earningsPage - 1))}
          disabled={earningsPage === 0}
          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
            earningsPage === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Indietro
        </button>
        <span className="text-gray-400 text-sm">
          Pagina {earningsPage + 1} di {Math.ceil(filteredEarnings.length / EARNINGS_PER_PAGE)}
        </span>
        <button
          onClick={() => setEarningsPage(Math.min(Math.ceil(filteredEarnings.length / EARNINGS_PER_PAGE) - 1, earningsPage + 1))}
          disabled={earningsPage >= Math.ceil(filteredEarnings.length / EARNINGS_PER_PAGE) - 1}
          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
            earningsPage >= Math.ceil(filteredEarnings.length / EARNINGS_PER_PAGE) - 1
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          Avanti
          <ChevronRight className="w-5 h-5 ml-1" />
        </button>
      </div>
    );
  };

  return (
    <div className="football-card glass-football p-6">
      <h2 className="text-2xl font-bold text-[#1e293b] mb-6">Referral Network</h2>
      {loading ? (
        <div className="text-center py-12">
          <div className="football-spinner mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading network...</p>
        </div>
      ) : network && allReferrals.length > 0 ? (
        <>
          <div className="space-y-4">
            {paginatedReferrals.map((ref) => (
              <div key={`${ref.level}-${ref.id}`} className="bg-gray-800 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-xs text-gray-400">{getLevelLabel(ref.level)}</span>
                    <div className="text-white font-medium">{ref.username}</div>
                  </div>
                  <span className="text-yellow-400 font-semibold">
                    {getReferralTotal(ref).toFixed(2)} DIOS
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {allReferrals.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-center space-x-4 mt-6">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                  currentPage === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 text-black hover:bg-yellow-500'
                }`}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </button>
              <span className="text-gray-400 text-sm">
                Page {currentPage + 1} of {Math.ceil(allReferrals.length / ITEMS_PER_PAGE)}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(allReferrals.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
                disabled={currentPage >= Math.ceil(allReferrals.length / ITEMS_PER_PAGE) - 1}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                  currentPage >= Math.ceil(allReferrals.length / ITEMS_PER_PAGE) - 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 text-black hover:bg-yellow-500'
                }`}
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 text-gray-400">
          No referrals yet. Share your referral code to invite friends!
        </div>
      )}

      <div className="border-t border-gray-700 my-8"></div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-[#1e293b]">
          Movimenti ({earningsTotal || earnings.length})
        </h3>
        <select
          value={earningsTypeFilter}
          onChange={(event) => {
            setEarningsTypeFilter(event.target.value);
            setEarningsPage(0);
          }}
          className="bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-600 text-sm"
        >
          <option value="">All types</option>
          <option value="buyin">Buy-in</option>
          <option value="marketplace_fee">Marketplace</option>
          <option value="pack">Pack</option>
          <option value="auction">Auction</option>
        </select>
      </div>
      {loading ? (
        <div className="text-center py-8">
          <div className="football-spinner mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading earnings...</p>
        </div>
      ) : filteredEarnings.length > 0 ? (
        <>
          {renderEarningsPagination()}
          <div className="space-y-3">
            {paginatedEarnings.map((entry) => (
              <div key={entry.id} className="bg-gray-800 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">
                      {formatDate(entry.created_at)}
                    </div>
                    <div className="text-white font-medium">
                      Level {entry.level} • {entry.earnings_type}
                    </div>
                    <div className="text-xs text-gray-400">
                      From: {entry.referred_username || `User #${entry.referred_id}`}
                    </div>
                  </div>
                  <span className="text-yellow-400 font-semibold">
                    +{parseFloat(entry.amount || 0).toFixed(2)} DIOS
                  </span>
                </div>
              </div>
            ))}
          </div>
          {renderEarningsPagination()}
        </>
      ) : (
        <div className="text-center py-8 text-gray-400">
          Nessun movimento registrato.
        </div>
      )}
    </div>
  );
};

export default ReferralsTab;
